import { useCallback, useId } from 'react';
import { X } from 'lucide-react';
import { Portal } from './portal';
import { Button } from './button';
import { ResizableModal } from './resizable-modal';
import { useBackdropClose } from './use-backdrop-close';
import { useOverlayFocus } from './overlay-focus';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Modal width preset */
  size?: ModalSize;
  /** Additional class names for the modal card */
  className?: string;
  /** Additional class names for the full-screen overlay (for nested modal stacking) */
  overlayClassName?: string;
  /** Additional class names for the content area (default includes p-6 padding) */
  contentClassName?: string;

  // ── Built-in footer ────────────────────────────────────
  /** Form submit handler — when set, children are wrapped in a <form> and a submit button appears in the footer */
  onSubmit?: (e: React.FormEvent) => void;
  /** Label for the primary action button (required for footer to appear) */
  submitLabel?: string;
  /** Label shown on the primary button while saving */
  submitLoadingLabel?: string;
  /** Whether the primary action is in progress */
  saving?: boolean;
  /** Disable the primary action button */
  submitDisabled?: boolean;
  /** Variant for the primary action button (default: 'primary') */
  submitVariant?: 'primary' | 'accent' | 'danger';
  /** Label for the cancel/close button in the footer */
  cancelLabel?: string;
  /** Completely custom footer — overrides the built-in footer */
  footer?: React.ReactNode;

  // ── Resize & guards ────────────────────────────────────
  /** Enable drag-to-resize with localStorage persistence */
  resizable?: boolean;
  /** Storage key for persisting resize dimensions (required when resizable) */
  storageKey?: string;
  /** Async guard called before close — return false to prevent closing */
  closeGuard?: () => Promise<boolean>;
  /** Hint text shown on first resize (only used when resizable) */
  resizeHintText?: string;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-4xl',
  xl: 'max-w-6xl',
  full: 'max-w-[95vw]',
};

export function Modal({
  title,
  onClose,
  children,
  size = 'md',
  className = '',
  overlayClassName = '',
  contentClassName = 'p-4 md:p-6',
  onSubmit,
  submitLabel,
  submitLoadingLabel,
  saving = false,
  submitDisabled = false,
  submitVariant = 'primary',
  cancelLabel,
  footer,
  resizable = false,
  storageKey,
  closeGuard,
  resizeHintText,
}: ModalProps) {
  const formId = useId();

  const handleClose = useCallback(async () => {
    if (closeGuard && !(await closeGuard())) return;
    onClose();
  }, [closeGuard, onClose]);

  const backdrop = useBackdropClose(handleClose);
  const overlayRef = useOverlayFocus<HTMLDivElement>({ onEscape: handleClose });

  const header = (
    <div className="flex items-center justify-between gap-3 px-4 md:px-6 pt-[calc(env(safe-area-inset-top)+1.25rem)] md:pt-5 pb-4 md:pb-5 border-b border-[var(--c-border)] shrink-0">
      <h2 className="min-w-0 font-display font-bold text-base md:text-lg leading-tight text-[var(--c-text)]">{title}</h2>
      <button
        onClick={handleClose}
        className="min-h-11 min-w-11 inline-flex items-center justify-center rounded-full hover:bg-[var(--c-overlay)] text-[var(--c-muted)] hover:text-[var(--c-text)] transition-colors"
        aria-label="Close"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );

  // When onSubmit is provided, wrap children in a <form> with auto-generated ID
  const wrappedChildren = onSubmit ? (
    <form id={formId} onSubmit={onSubmit} className="contents">
      {children}
    </form>
  ) : children;

  const content = (
    <div className={`overflow-y-auto flex-1 ${contentClassName}`} data-scroll-container>
      {wrappedChildren}
    </div>
  );

  // Build footer: custom footer > built-in from props > nothing
  let footerContent: React.ReactNode = null;
  if (footer) {
    footerContent = footer;
  } else if (submitLabel || cancelLabel) {
    footerContent = (
      <div className="flex flex-col sm:flex-row gap-3 justify-end">
        {cancelLabel && (
          <Button type="button" variant="secondary" onClick={handleClose}>
            {cancelLabel}
          </Button>
        )}
        {submitLabel && (
          <Button
            type="submit"
            form={onSubmit ? formId : undefined}
            variant={submitVariant}
            loading={saving}
            disabled={submitDisabled || saving}
          >
            {saving && submitLoadingLabel ? submitLoadingLabel : submitLabel}
          </Button>
        )}
      </div>
    );
  }

  const footerEl = footerContent ? (
    <div className="shrink-0 px-4 md:px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] md:pb-4 border-t border-[var(--c-border)]">
      {footerContent}
    </div>
  ) : null;

  if (resizable && storageKey) {
    return (
      <Portal lockBody>
        <div
          ref={overlayRef}
          className={`fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto overscroll-contain bg-[var(--c-card)]/95 backdrop-blur-lg p-0 md:p-4 animate-fade-in ${overlayClassName}`}
          onMouseDown={backdrop.onMouseDown}
          onMouseUp={backdrop.onMouseUp}
        >
          <ResizableModal
            storageKey={storageKey}
            onClose={onClose}
            closeGuard={closeGuard}
            resizeHintText={resizeHintText}
            aria-label={title}
            className={`h-[100dvh] md:h-auto md:max-h-[92vh] md:rounded-2xl bg-[var(--c-card)] shadow-2xl ${sizeClasses[size]} w-full min-h-0 ${className}`}
          >
            {header}
            {content}
            {footerEl}
          </ResizableModal>
        </div>
      </Portal>
    );
  }

  return (
    <Portal lockBody>
      <div
        ref={overlayRef}
        className={`fixed inset-0 z-50 flex items-start md:items-center justify-center overflow-y-auto overscroll-contain bg-[var(--c-card)]/95 backdrop-blur-lg p-0 md:p-4 ${overlayClassName}`}
        onMouseDown={backdrop.onMouseDown}
        onMouseUp={backdrop.onMouseUp}
      >
        <div
          data-modal-root="true"
          className={`relative w-full ${sizeClasses[size]} h-[100dvh] md:h-auto md:max-h-[92vh] md:rounded-2xl bg-[var(--c-card)] md:border border-[var(--c-border)] shadow-2xl flex flex-col overflow-hidden animate-slide-in ${className}`}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          {header}
          {content}
          {footerEl}
        </div>
      </div>
    </Portal>
  );
}

export type { ModalProps, ModalSize };
